import React, { useState, useEffect } from 'react';
import { Plus, X, UserCheck } from 'lucide-react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge
} from '../ui';
import { getAllCoaches } from '../../lib/supabase';
import { formatOrgRole } from '../../lib/utils';

const STAFF_ROLES = [
  { value: 'Head Coach', label: 'Head Coach' },
  { value: 'Asistente', label: 'Asistente' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Coach', label: 'Coach' }
];

const GROUP_ROLE_ALIASES = {
  'head coach': 'Head Coach',
  head_coach: 'Head Coach',
  'assistant coach': 'Asistente',
  assistant_coach: 'Asistente',
  assistant: 'Asistente',
  asistente: 'Asistente',
  manager: 'Manager',
  coach: 'Coach'
};

export const normalizeGroupRole = (role) => {
  if (!role) return 'Coach';
  const normalized = String(role).trim();
  const alias = GROUP_ROLE_ALIASES[normalized.toLowerCase()];
  if (alias) return alias;
  const exact = STAFF_ROLES.find((item) => item.value === normalized);
  return exact ? exact.value : 'Coach';
};

/**
 * StaffSelector - Component for selecting multiple staff members with roles
 *
 * IMPORTANTE: Este componente maneja DOS campos "rol" diferentes:
 * 1. staff.rol - Tipo de staff en la organización (viene de AppSheet: "coach", "manager", "admin")
 *                Este campo NO se modifica aquí, solo se muestra como referencia.
 * 2. assignment.rol - Rol del staff EN ESTE GRUPO específico (se guarda en staff_grupos)
 *                     Opciones: "Head Coach", "Asistente", "Manager", "Coach"
 *                     Este SÍ se puede modificar y se guarda en la tabla staff_grupos.
 *
 * @param {Array} selectedStaff - Array of {staff_id, rol, staff} objects
 *                                - staff_id: ID del staff member
 *                                - rol: Rol EN EL GRUPO (se guarda en staff_grupos.rol)
 *                                - staff: Objeto con info del staff (incluye staff.rol de AppSheet)
 * @param {Function} onChange - Callback when staff selection changes
 * @param {string} className - Additional CSS classes
 */
const StaffSelector = ({ selectedStaff = [], onChange, className = '', onError }) => {
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStaffId, setNewStaffId] = useState('');
  const [newStaffRol, setNewStaffRol] = useState('Coach');

  // Load all staff on mount
  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const { data, error } = await getAllCoaches();

      if (error) {
        console.error('Error loading staff:', error);
        return;
      }

      setAllStaff(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get staff that haven't been selected yet
  const availableStaff = allStaff.filter(
    staff => staff.activo !== false &&
      !selectedStaff.some(selected => selected.staff_id === staff.id)
  );

  const handleAddStaff = () => {
    if (!newStaffId) return;

    const staffMember = allStaff.find(s => s.id === newStaffId);
    if (!staffMember) return;

    const newSelection = [
      ...selectedStaff,
      {
        staff_id: newStaffId,
        rol: normalizeGroupRole(newStaffRol),
        staff: staffMember,
        tarjeton_entrenador: false,
        tarjeton_asistente: false
      }
    ];

    onChange(newSelection);
    setNewStaffId('');
    setNewStaffRol('Coach');
  };

  const handleRemoveStaff = (staffId) => {
    const newSelection = selectedStaff.filter(s => s.staff_id !== staffId);
    onChange(newSelection);
  };

  const notifyError = (message) => {
    if (onError) {
      onError('Error', message);
      return;
    }
    console.warn(message);
  };

  const resolveTarjetonRole = (role) => {
    const normalized = normalizeGroupRole(role);
    if (normalized === 'Asistente') return 'asistente';
    if (normalized === 'Manager') return 'none';
    return 'entrenador';
  };

  const handleUpdateRole = (staffId, newRol) => {
    const normalizedRole = normalizeGroupRole(newRol);
    const newSelection = selectedStaff.map((assignment) => {
      if (assignment.staff_id !== staffId) return assignment;
      const tarjetonType = resolveTarjetonRole(normalizedRole);
      const hasTarjeton = assignment.tarjeton_entrenador || assignment.tarjeton_asistente;

      if (tarjetonType === 'none' && hasTarjeton) {
        return { ...assignment, rol: normalizedRole, tarjeton_entrenador: false, tarjeton_asistente: false };
      }

      if (tarjetonType === 'asistente' && assignment.tarjeton_entrenador) {
        return { ...assignment, rol: normalizedRole, tarjeton_entrenador: false, tarjeton_asistente: true };
      }

      if (tarjetonType === 'entrenador' && assignment.tarjeton_asistente) {
        return { ...assignment, rol: normalizedRole, tarjeton_entrenador: true, tarjeton_asistente: false };
      }

      return { ...assignment, rol: normalizedRole };
    });

    const hasMultipleAssistants = newSelection.filter(
      (assignment) => assignment.tarjeton_asistente
    ).length > 1;
    const hasMultipleCoaches = newSelection.filter(
      (assignment) => assignment.tarjeton_entrenador
    ).length > 1;

    if (hasMultipleAssistants || hasMultipleCoaches) {
      notifyError('Solo puede haber un entrenador y un asistente en el tarjeton.');
      return;
    }

    onChange(newSelection);
  };

  const toggleTarjeton = (staffId, value) => {
    const newSelection = selectedStaff.map((assignment) => {
      if (assignment.staff_id !== staffId) return assignment;
      const tarjetonType = resolveTarjetonRole(assignment.rol);
      if (!value) {
        return { ...assignment, tarjeton_entrenador: false, tarjeton_asistente: false };
      }
      if (tarjetonType === 'none') {
        notifyError('El rol Manager no puede aparecer en el tarjeton.');
        return assignment;
      }
      return {
        ...assignment,
        tarjeton_entrenador: tarjetonType === 'entrenador',
        tarjeton_asistente: tarjetonType === 'asistente'
      };
    });

    const hasMultipleAssistants = newSelection.filter(
      (assignment) => assignment.tarjeton_asistente
    ).length > 1;
    const hasMultipleCoaches = newSelection.filter(
      (assignment) => assignment.tarjeton_entrenador
    ).length > 1;

    if (hasMultipleAssistants || hasMultipleCoaches) {
      notifyError('Solo puede haber un entrenador y un asistente en el tarjeton.');
      return;
    }

    onChange(newSelection);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* List of selected staff */}
      {selectedStaff.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Staff Asignado ({selectedStaff.length})
          </label>
          {selectedStaff.map((assignment) => (
            <div
              key={assignment.staff_id}
              className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <UserCheck className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {assignment.staff?.nombre_completo || 'Staff member'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {assignment.staff?.rol && (
                      <span className="font-medium capitalize">
                        {formatOrgRole(assignment.staff.rol)}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex w-full lg:w-auto items-center gap-2">
                {/* Role selector - Rol EN EL GRUPO (se guarda en staff_grupos.rol) */}
                <Select
                  value={normalizeGroupRole(assignment.rol)}
                  onValueChange={(value) => handleUpdateRole(assignment.staff_id, value)}
                >
                  <SelectTrigger className="w-full sm:w-40 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAFF_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Remove button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveStaff(assignment.staff_id)}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center justify-start lg:justify-end gap-3 text-xs text-gray-600 min-w-0">
                <button
                  type="button"
                  role="switch"
                  aria-checked={assignment.tarjeton_entrenador === true || assignment.tarjeton_asistente === true}
                  aria-label="Aparece en tarjeton"
                  onClick={() =>
                    toggleTarjeton(
                      assignment.staff_id,
                      !(assignment.tarjeton_entrenador === true || assignment.tarjeton_asistente === true)
                    )
                  }
                  disabled={resolveTarjetonRole(assignment.rol) === 'none'}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                    assignment.tarjeton_entrenador === true || assignment.tarjeton_asistente === true
                      ? 'bg-emerald-600'
                      : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      assignment.tarjeton_entrenador === true || assignment.tarjeton_asistente === true
                        ? 'translate-x-5'
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className="text-xs whitespace-nowrap select-none">
                  Aparece en tarjeton
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new staff section */}
      {availableStaff.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Agregar Staff
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Staff selector */}
            <Select
              value={newStaffId}
              onValueChange={setNewStaffId}
              disabled={loading}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder={loading ? "Cargando..." : "Seleccionar staff..."} />
              </SelectTrigger>
              <SelectContent>
                {availableStaff.map(staff => {
                  const displayName = staff.rol
                    ? `${staff.nombre_completo} (${formatOrgRole(staff.rol)})`
                    : staff.nombre_completo;
                  return (
                    <SelectItem key={staff.id} value={staff.id}>
                      {displayName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Role selector */}
            <Select value={normalizeGroupRole(newStaffRol)} onValueChange={setNewStaffRol}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAFF_ROLES.map(role => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Add button */}
            <Button
              type="button"
              onClick={handleAddStaff}
              disabled={!newStaffId}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 sm:mr-0" />
              <span className="sm:hidden ml-2">Agregar</span>
            </Button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {selectedStaff.length === 0 && (
        <div className="text-center py-4 text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          No hay staff asignado. Agrega coaches o managers para este grupo.
        </div>
      )}

      {/* No more staff available */}
      {availableStaff.length === 0 && selectedStaff.length > 0 && (
        <p className="text-xs text-gray-500 italic">
          Todo el staff disponible ha sido asignado
        </p>
      )}
    </div>
  );
};

export default StaffSelector;


